import EventEmitter from 'eventemitter3';

import { config } from './config';
import { sendGAEvent } from './google-analytics';

type OptimizeEvents = {
  optimizeTriggered: any;
  variantsChanged: [Record<string, string>];
};

export const optimizeExperiments = {
  estimateLandingPage2: 'npqPJOxvSwes2c4CiWWDVg',
  rpSimilarEstates: 'lnCkE3q9RbizfbijcpbqAQ',
  buyerFormMortgageSteps: '6nKSMYdbTIKQdXv0TrDu0A',
} as const;

export const isOptimizeExperimentName = (
  value: string,
): value is OptimizeExperimentName => {
  return Object.keys(optimizeExperiments).includes(value);
};

export type OptimizeExperimentName = keyof typeof optimizeExperiments;

export type OptimizeExperimentIds =
  typeof optimizeExperiments[OptimizeExperimentName];

export const optimizeEvents = new EventEmitter<OptimizeEvents>();

export const triggerOptimizeEvent = (event = 'optimize.activate') => {
  if (!window.dataLayer) return false;

  window.dataLayer.push({ event });

  optimizeEvents.emit('optimizeTriggered');
};

export type RefreshParams = {
  triggerEvent?: boolean | string;
  /** Unique ID to prevent single experiment from triggering multiple times */
  id?: string;
};

const triggeredTests: string[] = [];

export const isOptimizeReady = () => {
  if (typeof window === 'undefined') return false;
  if (!config.GTM_ENABLED) {
    return true;
  }

  if (!window.dataLayer) return false;

  // Check for `optimize.callback` event in dataLayer
  // this event indicates that optimize has been loaded
  // (it's a custom event triggered by our google-optimize provider)
  if (Array.isArray(window.dataLayer)) {
    return window.dataLayer.some(
      (v) =>
        (Array.isArray(v) && v.some((e) => e === 'optimize.callback')) ||
        v[1] === 'optimize.callback',
    );
  }
};

export const experimentsToTrigger: RefreshParams[] = [];

export const refreshOptimizeVariants = async ({
  triggerEvent = true,
  id,
}: RefreshParams = {}) => {
  if (id) {
    if (triggeredTests.includes(id)) return;

    triggeredTests.push(id);
  }

  sendGAEvent('optimize', 'variants requested', 'trigger');

  return new Promise<undefined | Record<string, string>>((resolve) => {
    let resolved = false;
    optimizeEvents.once('variantsChanged', (variants) => {
      if (!resolved) {
        resolved = true;
        sendGAEvent('optimize', 'variants received', 'trigger');
        resolve(variants);
      }
    });
    if (triggerEvent)
      triggerOptimizeEvent(
        typeof triggerEvent === 'string' ? triggerEvent : undefined,
      );
    setTimeout(() => {
      if (!resolved) {
        resolved = true;
        sendGAEvent('optimize', 'variants timeout', 'trigger');
        resolve(undefined);
      }
    }, 1500);
  });
};

export const isOptimizeVariant = (
  experimentName: keyof typeof optimizeExperiments,
  variant: 'a' | 'b' | 'c',
  optimizeVariants: { [experiment: string]: string },
): boolean => {
  const experiment = optimizeExperiments[experimentName];
  if (!optimizeVariants || !optimizeVariants[experiment]) return false;

  const charValue = variant.charCodeAt(0) - 'a'.charCodeAt(0);
  return optimizeVariants[experiment] === `${charValue}`;
};
